import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { db } from "../../app/firebase.config";
import { collection, getDocs } from "firebase/firestore";
import { mapImageURL } from "../../utils/mapImageURL";
import { fetchTeachLeagueFolders } from "../../utils/lessonPlanApi";
import { driveFoldersToCourses } from "../../utils/driveFolderToCourse";

export const fetchCourses = createAsyncThunk(
  "courses/fetchCourses",
  async () => {
    const querySnapshot = await getDocs(collection(db, "courses"));
    const courses = [];
    querySnapshot.forEach((doc) => {
      courses.push(doc.data());
    });

    return courses;
  }
);

// Fetch Drive folders as courses
export const fetchDriveCourses = createAsyncThunk(
  "courses/fetchDriveCourses",
  async () => {
    try {
      const result = await fetchTeachLeagueFolders({ limit: 100 });
      const folders = result?.folders || [];
      return driveFoldersToCourses(folders);
    } catch (error) {
      console.error('Error fetching Drive courses:', error);
      return [];
    }
  },
  {
    // Drive courses require an authenticated user.
    // Prevent dispatching pending/rejected noise when logged out.
    condition: (_, { getState }) => {
      const uid = getState()?.user?.currentUser?.uid;
      return Boolean(uid);
    },
  }
);

const initialState = {
  coursesArray: [],
  driveCourses: [],
  isLoading: true,
  isLoadingDrive: false,
  errMsg: "",
};

const coursesSlice = createSlice({
  name: "courses",
  initialState,
  reducers: {
    // Manual reducer to merge Drive courses with regular courses
    mergeDriveCourses: (state) => {
      // Merge driveCourses into coursesArray, avoiding name-based duplicates
      // Prefer existing courses (likely Firestore) over Drive courses with same name
      const existingCourseNames = new Set(
        state.coursesArray.map(c => c.name.toLowerCase().trim())
      );
      const uniqueDriveCourses = state.driveCourses.filter(
        dc => !existingCourseNames.has(dc.name.toLowerCase().trim())
      );
      state.coursesArray = [...state.coursesArray, ...uniqueDriveCourses];
    },
  },
  extraReducers: (builder) => {
    builder
      // Regular courses
      .addCase(fetchCourses.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.errMsg = "";
        const allFirestoreCourses = mapImageURL(action.payload);

        // Deduplicate Firestore courses by name (keep first occurrence)
        const seenNames = new Set();
        const regularCourses = allFirestoreCourses.filter(course => {
          const normalizedName = course.name.toLowerCase().trim();
          if (seenNames.has(normalizedName)) {
            console.warn(`⚠️ [Deduplication] Skipping duplicate Firestore course: "${course.name}" (ID: ${course.id})`);
            return false;
          }
          seenNames.add(normalizedName);
          return true;
        });

        // Merge with Drive courses, avoiding name-based duplicates
        // Prefer Firestore courses over Drive courses with the same name
        const firestoreCourseNames = new Set(
          regularCourses.map(c => c.name.toLowerCase().trim())
        );
        const uniqueDriveCourses = state.driveCourses.filter(
          dc => !firestoreCourseNames.has(dc.name.toLowerCase().trim())
        );

        console.log('[Deduplication] Firestore courses loaded:', allFirestoreCourses.length);
        console.log('[Deduplication] Unique Firestore courses after dedup:', regularCourses.length);
        console.log('[Deduplication] Drive courses available:', state.driveCourses.length);
        console.log('[Deduplication] Unique Drive courses after filter:', uniqueDriveCourses.length);

        state.coursesArray = [...regularCourses, ...uniqueDriveCourses];
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.isLoading = false;
        state.errMsg = action.error ? action.error.message : "Fetch failed";
      })
      // Drive courses
      .addCase(fetchDriveCourses.pending, (state) => {
        state.isLoadingDrive = true;
      })
      .addCase(fetchDriveCourses.fulfilled, (state, action) => {
        state.isLoadingDrive = false;
        state.driveCourses = action.payload;

        // Merge with existing regular courses, avoiding name-based duplicates
        // Prefer Firestore courses over Drive courses with the same name
        const firestoreCourses = state.coursesArray.filter(c => c.source !== 'drive');
        const firestoreCourseNames = new Set(
          firestoreCourses.map(c => c.name.toLowerCase().trim())
        );
        const uniqueDriveCourses = action.payload.filter(
          dc => !firestoreCourseNames.has(dc.name.toLowerCase().trim())
        );

        console.log('[Deduplication] Drive courses loaded:', action.payload.length);
        console.log('[Deduplication] Firestore courses in array:', firestoreCourses.length);
        console.log('[Deduplication] Unique Drive courses after filter:', uniqueDriveCourses.length);
        console.log('[Deduplication] Drive course names:', action.payload.map(c => c.name));
        console.log('[Deduplication] Firestore course names:', Array.from(firestoreCourseNames));

        // Log any duplicates found
        const duplicates = action.payload.filter(
          dc => firestoreCourseNames.has(dc.name.toLowerCase().trim())
        );
        if (duplicates.length > 0) {
          console.log('[Deduplication] Found duplicates (filtered out):', duplicates.map(d => d.name));
        }

        state.coursesArray = [...firestoreCourses, ...uniqueDriveCourses];
      })
      .addCase(fetchDriveCourses.rejected, (state, action) => {
        state.isLoadingDrive = false;
        console.error('Failed to fetch Drive courses:', action.error);
      });
  },
});

export const { mergeDriveCourses } = coursesSlice.actions;
export const coursesReducer = coursesSlice.reducer;

export const selectAllCourses = (state) => {
  return state.courses.coursesArray;
};

export const selectRandomCourse = (state) => {
  const coursesArray = state.courses.coursesArray;
  const randomIndex = Math.floor(coursesArray.length * Math.random());

  return coursesArray[randomIndex];
};

export const selectCourseById = (id) => (state) => {
  // Handle Drive courses (IDs like "drive-xyz") and regular courses (numeric IDs)
  return state.courses.coursesArray.find(
    (course) => {
      if (typeof id === 'string' && id.startsWith('drive-')) {
        return course.id === id;
      }
      return course.id === parseInt(id) || course.id === id;
    }
  );
};

export const selectFeaturedCourse = (state) => {
  return {
    icon: "📓 ",
    title: "Resources",
    subtitle:
      "I'm a teacher. I get it. If you need help with your content I've got you so you don’t lose it.",
    link: "directory",
    featuredItem: state.courses.coursesArray.find((course) => course.featured),
    isLoading: state.courses.isLoading,
    errMsg: state.courses.errMsg,
  };
};

export const selectFreeCourse = (state) => {
  return {
    freeItem: state.courses.coursesArray.find((course) => course.free),
    isLoading: state.courses.isLoading,
    errMsg: state.courses.errMsg,
  };
};

export const shortSelectFeaturedCourse = (state) => {
  return selectFeaturedCourse.slice(0, 20);
};

export const selectGovernmentCourses = (state) => {
  return {
    freeItem: state.courses.coursesArray.filter((course) =>
      course.credit.includes("Government")
    ),
    isLoading: state.courses.isLoading,
    errMsg: state.courses.errMsg,
  };
};

export const selectWorldHistoryCourses = (state) => {
  return {
    freeItem: state.courses.coursesArray.filter((course) =>
      course.credit.includes("World History")
    ),
    isLoading: state.courses.isLoading,
    errMsg: state.courses.errMsg,
  };
};

export const selectUSHistoryCourses = (state) => {
  return {
    freeItem: state.courses.coursesArray.filter((course) =>
      course.credit.includes("US History")
    ),
    isLoading: state.courses.isLoading,
    errMsg: state.courses.errMsg,
  };
};

export const selectGeographyCourses = (state) => {
  return {
    freeItem: state.courses.coursesArray.filter((course) =>
      course.credit.includes("Geography")
    ),
    isLoading: state.courses.isLoading,
    errMsg: state.courses.errMsg,
  };
};

export const selectMandatoryCourses = (state) => {
  return {
    freeItem: state.courses.coursesArray.filter((course) =>
      course.credit.includes("Mandatory")
    ),
    isLoading: state.courses.isLoading,
    errMsg: state.courses.errMsg,
  };
};

export const selectLanguageArtsCourses = (state) => {
  return {
    freeItem: state.courses.coursesArray.filter((course) =>
      course.credit.includes("Language Arts")
    ),
    isLoading: state.courses.isLoading,
    errMsg: state.courses.errMsg,
  };
};

export const selectElectiveCourses = (state) => {
  return {
    freeItem: state.courses.coursesArray.filter((course) =>
      course.credit.includes("Elective")
    ),
    isLoading: state.courses.isLoading,
    errMsg: state.courses.errMsg,
  };
};
